import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from "axios";
import {
  getAccessToken,
  getActiveTenantId,
  redirectToLoginPreservingPath,
  refreshAccessToken,
} from "./auth-bridge";
import { ApiError, normalizeError } from "./errors";
import { logger } from "@/lib/logger";
import { getApiBaseUrl, getUseMocks } from "@/lib/runtime-config";

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Evita disparar el redirect de recuperación más de una vez cuando varias
 * peticiones en paralelo (p.ej. el dashboard de BI) fallan con 401 a la vez.
 */
let sessionRecoveryTriggered = false;
function recoverSessionOnce(): void {
  if (sessionRecoveryTriggered) return;
  sessionRecoveryTriggered = true;
  redirectToLoginPreservingPath();
}

const baseURL = getApiBaseUrl();

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
});

/** Espera breve a que el AuthClient quede registrado y exponga un token. */
const TOKEN_WAIT_MS = 2_000;
const TOKEN_POLL_MS = 100;

async function resolveToken(): Promise<string | null> {
  const deadline = Date.now() + TOKEN_WAIT_MS;
  for (;;) {
    const token = await getAccessToken();
    if (token) return token;
    if (Date.now() >= deadline) return null;
    await new Promise((resolve) => setTimeout(resolve, TOKEN_POLL_MS));
  }
}

api.interceptors.request.use(async (config) => {
  const token = await resolveToken();
  if (!token) {
    // No enviamos peticiones sin Authorization: el backend responde 401 y la UI
    // termina renderizando datos incompletos. Abortamos con un motivo explícito.
    logger.warn("[API] Petición abortada sin sesión activa:", config.url ?? "");
    throw new ApiError({
      status: 401,
      code: "AUTH_NO_TOKEN",
      message: "No hay sesión activa para consultar el servicio",
    });
  }
  config.headers.set("Authorization", `Bearer ${token}`);
  const tenantId = getActiveTenantId();
  if (tenantId) {
    config.headers.set("X-Tenant-Id", tenantId);
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err instanceof ApiError) return Promise.reject(err);
    const axiosErr = err as AxiosError<{ message?: string }>;
    const status = axiosErr.response?.status;

    if (status === 401) {
      const config = axiosErr.config as RetryableConfig | undefined;
      const method = (config?.method ?? "get").toUpperCase();
      const url = config?.url ?? "";
      const backendMessage = axiosErr.response?.data?.message ?? "(sin mensaje del backend)";
      logger.warn(`[API] 401 ${method} ${url} — "${backendMessage}"`);

      // Un 401 del BFF no invalida por sí solo la sesión SSO: el access token
      // pudo expirar sin que el heartbeat del SDK alcanzara a renovarlo (p.ej.
      // pestaña en background). Reintentamos una sola vez tras forzar el
      // refresh, de forma silenciosa (sin redirect ni reload) para no
      // interrumpir la UI. Si el refresh falla, se deja pasar el 401 normal.
      if (config && !config._retry) {
        config._retry = true;
        try {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            const newToken = await getAccessToken();
            if (newToken) {
              config.headers.set("Authorization", `Bearer ${newToken}`);
              return api.request(config);
            }
          }
          // Refresh sin éxito (o sin refresh token disponible): la sesión no
          // puede renovarse en silencio. Único caso en que este cliente
          // navega — evita que la pantalla quede muerta esperando datos que
          // ya nunca llegarán en cualquier módulo (Motor IA, BI, etc.).
          recoverSessionOnce();
        } catch (refreshErr) {
          logger.warn("[API] Refresh de token tras 401 falló:", refreshErr);
          recoverSessionOnce();
        }
      }
    }

    return Promise.reject(normalizeError(err));
  },
);


export const useMockFallback = getUseMocks();

export const hasApiBaseUrl = Boolean(baseURL);

/** True si el error corresponde a falta de sesión (sin token o 401 del backend). */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Helper: intenta la llamada al BFF; si no hay base URL o falla y el fallback
 * está habilitado, devuelve el resultado del mock.
 */
export async function withFallback<T>(
  call: () => Promise<T>,
  mock: () => T | Promise<T>,
): Promise<T> {
  if (!hasApiBaseUrl) {
    if (useMockFallback) return await mock();
    throw new Error("API_BASE_URL no configurada y mock fallback deshabilitado");
  }
  try {
    return await call();
  } catch (err) {
    if (useMockFallback) return await mock();
    throw err;
  }
}
